/**
 * ScnSpecGroup derivation (S7.1) — THE single derivation source for SCN
 * group membership.
 *
 * Pure, zero I/O (the `resolvePrice.ts` precedent): the caller supplies the
 * products collection and the seeded spec metadata; every consumer — the
 * `GET /scn-groups[/:id]` MSW handler, the Spec Master surfaces (S7.2) and
 * any test — derives membership through this ONE function, so the Spec
 * Master can never drift from `useScnGroups` (which groups the same
 * `product.scn` column client-side).
 *
 * Read-model semantics:
 * - Codes are the UNION of seeded spec codes and codes present on products.
 *   A seeded group whose members all unlink STAYS listed (spec metadata is
 *   first-class and survives membership churn — board A2 renders the 0-SKU
 *   row as 未啟用); a members-only group (no seeded spec yet) disappears
 *   once its last member unlinks (nothing left to derive it from).
 * - Membership is NEVER stored on the group — it is recomputed from
 *   `product.scn` on every call (derive-never-restore; retroactive PATCH
 *   link/unlink is instantly reflected, past documents keep their own
 *   references).
 * - `status` / `memberCount` / `oemNumbers` / `markets` / `updatedAt` are
 *   roll-ups derived from the members (+ seeded `updatedAt`).
 */

import type { Product } from '@/types/api/product';
import type { ScnSpecGroup, ScnSpecMeta } from '@/types/api/scn';

/**
 * Market of a subsidiary — the `OEM所屬國家/市場` axis of the cross-market
 * twins (SCN pairs are TW + AU records of the same shared product). Derived
 * from the `SUB-<MARKET>-<name>` id shape used by the seeds; falls back to
 * the raw id when the shape doesn't match (never throws on display data).
 */
export function marketOfSubsidiary(subsidiaryId: string): string {
  const match = /^SUB-([A-Z]+)-/.exec(subsidiaryId);
  return match ? match[1] : subsidiaryId;
}

/** Unique values, first-seen order preserved (deterministic roll-ups). */
function unique(values: string[]): string[] {
  return [...new Set(values)];
}

/** Latest ISO timestamp of the candidates (string compare is safe for ISO). */
function latest(timestamps: string[]): string {
  return timestamps.reduce((max, ts) => (ts > max ? ts : max), timestamps[0] ?? '');
}

/**
 * Build the full Spec Master read model from the products collection and the
 * seeded spec metadata. Sorted by SCN code (the `useScnGroups` sort).
 *
 * Throws on duplicate spec metadata for one code — the seed contract is
 * "seeded ONCE" (a duplicate would silently shadow spec fields).
 */
export function buildScnSpecGroups(
  products: readonly Product[],
  specMeta: readonly ScnSpecMeta[]
): ScnSpecGroup[] {
  const metaByScn = new Map<string, ScnSpecMeta>();
  for (const meta of specMeta) {
    if (metaByScn.has(meta.scn)) {
      throw new Error(`buildScnSpecGroups: duplicate ScnSpecMeta for ${meta.scn} (seed-once contract)`);
    }
    metaByScn.set(meta.scn, meta);
  }

  const membersByScn = new Map<string, Product[]>();
  for (const product of products) {
    if (!product.scn) continue;
    const members = membersByScn.get(product.scn);
    if (members) members.push(product);
    else membersByScn.set(product.scn, [product]);
  }

  const codes = unique([...metaByScn.keys(), ...membersByScn.keys()]).sort((a, b) =>
    a.localeCompare(b)
  );

  return codes.map((scn) => {
    const meta = metaByScn.get(scn);
    const members = membersByScn.get(scn) ?? [];
    return {
      scn,
      specName: meta?.specName ?? null,
      specNameZh: meta?.specNameZh ?? null,
      majorCategory: meta?.majorCategory ?? null,
      specDescription: meta?.specDescription ?? null,
      // Board A2 rule: the 0-SKU row renders 未啟用.
      status: members.length > 0 ? 'in_use' : 'inactive',
      memberCount: members.length,
      oemNumbers: unique(members.map((m) => m.oemNo ?? '').filter(Boolean)),
      markets: unique(members.map((m) => marketOfSubsidiary(m.subsidiaryId))),
      updatedAt: latest([...(meta ? [meta.updatedAt] : []), ...members.map((m) => m.updatedAt)]),
      members,
    };
  });
}

/**
 * List-search predicate (board A2:「搜尋 SCN 代碼或規格名稱...」) — matches
 * the SCN code and both spec-name mirrors, case-insensitive. Shared by the
 * handler and any client-side filtering so search semantics never fork.
 */
export function matchesScnSearch(group: ScnSpecGroup, term: string): boolean {
  const needle = term.trim().toLowerCase();
  if (!needle) return true;
  return [group.scn, group.specName ?? '', group.specNameZh ?? ''].some((value) =>
    value.toLowerCase().includes(needle)
  );
}
